import { fetchZipDownloadData, type ZipDownloadData } from "../state/audit";

export interface ZipProgress {
  step: string;
  percentage: number;
}

export interface ZipGeneratorResult {
  blob: Blob;
  filename: string;
}

/**
 * Orchestrates ZIP download for a supplier.
 * Fetches data, delegates to web worker for ZIP generation.
 */
export async function generateSupplierZip(
  supplierId: number,
  onProgress: (progress: ZipProgress) => void,
): Promise<ZipGeneratorResult> {
  // Step 1: Fetch all data needed for ZIP
  onProgress({ step: "Fetching proof data...", percentage: 5 });
  const data = await fetchZipDownloadData(supplierId);

  if (data.activities.length === 0) {
    throw new Error("No approved activities found for this supplier.");
  }

  // Count total images for progress tracking
  let totalImages = 0;
  for (const activity of data.activities) {
    for (const sa of activity.storeActivities) {
      totalImages += sa.proofs.length;
    }
  }

  if (totalImages === 0) {
    throw new Error("No proof images found for this supplier.");
  }

  onProgress({ step: "Preparing images...", percentage: 10 });

  // Step 2: Delegate to web worker for ZIP generation
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL("./zip-worker.ts", import.meta.url), {
      type: "module",
    });

    worker.onmessage = (event) => {
      const { type, data: msgData } = event.data;

      switch (type) {
        case "progress": {
          // Map worker progress (0-100) to our range (10-95)
          const mappedPercentage = 10 + msgData.percentage * 0.85;
          onProgress({
            step: msgData.step,
            percentage: Math.round(mappedPercentage),
          });
          break;
        }

        case "complete":
          onProgress({ step: "Finalizing...", percentage: 98 });
          worker.terminate();
          resolve({
            blob: msgData.blob,
            filename: `${sanitizeFilename(data.supplierName)}.zip`,
          });
          break;

        case "error":
          worker.terminate();
          reject(new Error(msgData.message));
          break;
      }
    };

    worker.onerror = (err) => {
      worker.terminate();
      reject(new Error(`Worker error: ${err.message}`));
    };

    // Send data to worker
    worker.postMessage({
      type: "start",
      data: prepareWorkerData(data),
    });
  });
}

/**
 * Prepare data for the web worker.
 * Transforms the data into a flat list of images with their target paths.
 */
interface WorkerImageData {
  url: string;
  path: string; // Full path in ZIP
}

interface WorkerInput {
  supplierName: string;
  images: WorkerImageData[];
}

function prepareWorkerData(data: ZipDownloadData): WorkerInput {
  const images: WorkerImageData[] = [];

  for (const activity of data.activities) {
    // Build folder name: ${Period name}_${Product name}_${MarketingActivityType}
    const folderName = sanitizeFilename(
      `${activity.periodName}_${activity.productName}_${activity.activityTypeName}`,
    );

    for (const sa of activity.storeActivities) {
      for (const proof of sa.proofs) {
        // Build filename: ${timestamp}_${reference}_${type}.jpeg
        const timestamp = formatTimestampForFilename(proof.timestamp);
        const reference = sanitizeFilename(sa.storeReference || "unknown");
        const filename = `${timestamp}_${reference}_${proof.code}.jpeg`;

        images.push({
          url: proof.imageUrl,
          path: `${sanitizeFilename(data.supplierName)}/${folderName}/${filename}`,
        });
      }
    }
  }

  return {
    supplierName: data.supplierName,
    images,
  };
}

/**
 * Format timestamp for filename: ISO format with colons → underscores.
 * Example: 2026-01-15T14:30:25.123Z → 2026-01-15T14_30_25Z
 */
function formatTimestampForFilename(timestamp: string): string {
  // Parse and format to ISO without milliseconds
  const date = new Date(timestamp);
  const iso = date.toISOString();
  // Remove milliseconds and replace colons with underscores
  return iso.replace(/\.\d{3}Z$/, "Z").replace(/:/g, "_");
}

/**
 * Sanitize a string for use as a filename.
 * Removes/replaces characters that are invalid in filenames.
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, "_") // Replace invalid chars
    .replace(/\s+/g, "_") // Replace whitespace with underscore
    .replace(/_+/g, "_") // Collapse multiple underscores
    .replace(/^_|_$/g, "") // Trim leading/trailing underscores
    .substring(0, 200); // Limit length
}
